import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { MOCK_APPLICATIONS } from '../mocks/application.mock';
import { MOCK_PAGER_DUTY_SERVICES } from '../mocks/pagerDutyServices.mock';
import { MOCK_SERVICE_CONFIGS } from '../mocks/service-config.mock';
import { Application, CreateApplicationDto, UpdateApplicationDto } from '../models/application.model';
import { Feature, CreateFeatureDto, UpdateFeatureDto } from '../models/feature.model';
import { Incident } from '../models/incident.model';
import { ServiceConfig } from '../models/service-config.model';
import { PagerDutyService } from '../models/pagerDutyService.model';
import { MOCK_INCIDENTS, MOCK_PAGINATED_RESPONSES } from '../mocks/incident.mock';
import { PaginatedResponse } from '../models/paginated-response.model';

@Injectable({
  providedIn: 'root'
})
export class MockDataService {

  /**
   * Returns mock pagerDutyServices with simulated API delay
   * This mimics the /incidents/v1/pagerDutyServices endpoint
   */
  getPagerDutyServices(): Observable<HttpResponse<PagerDutyService[]>> {
    const mockResponse = new HttpResponse({
      body: [...MOCK_PAGER_DUTY_SERVICES],
      status: 200
    });
    return of(mockResponse).pipe(
      delay(500)
    );
  }

  /**
   * Get PagerDutyService(s) by pagerDutyServiceId
   */
  getPagerDutyServiceById(pagerDutyServiceId: string): Observable<HttpResponse<PagerDutyService[]>> {
    const services = MOCK_PAGER_DUTY_SERVICES.filter(service => service.pagerDutyServiceId === pagerDutyServiceId);
    const mockResponse = new HttpResponse({
      body: services,
      status: services.length > 0 ? 200 : 404
    });
    return of(mockResponse).pipe(delay(300));
  }

  /**
   * Returns mock service configurations with simulated API delay
   */
  getServiceConfigs(): Observable<HttpResponse<ServiceConfig[]>> {
    const mockResponse = new HttpResponse({
      body: [...MOCK_SERVICE_CONFIGS],
      status: 200
    });
    return of(mockResponse).pipe(
      delay(500)
    );
  }

  /**
   * Get a single service config by ID
   */
  getServiceConfig(pagerDutyServiceId: string): Observable<HttpResponse<ServiceConfig>> {
    const config = MOCK_SERVICE_CONFIGS.find(config => config.pagerDutyServiceId === pagerDutyServiceId);
    const mockResponse = new HttpResponse({
      body: config,
      status: config ? 200 : 404
    });
    return of(mockResponse).pipe(delay(300));
  }

  /**
   * Patch/update an existing service config
   */
  patchServiceConfig(pagerDutyServiceId: string, serviceConfig: Partial<ServiceConfig>): Observable<HttpResponse<ServiceConfig>> {
    const index = MOCK_SERVICE_CONFIGS.findIndex(config => config.pagerDutyServiceId === pagerDutyServiceId);

    if (index !== -1) {
      MOCK_SERVICE_CONFIGS[index] = {
        ...MOCK_SERVICE_CONFIGS[index],
        ...serviceConfig,
        pagerDutyServiceId: MOCK_SERVICE_CONFIGS[index].pagerDutyServiceId,
        pagerDutyServiceName: MOCK_SERVICE_CONFIGS[index].pagerDutyServiceName
      };

      const mockResponse = new HttpResponse({
        body: MOCK_SERVICE_CONFIGS[index],
        status: 200
      });
      return of(mockResponse).pipe(delay(400));
    }

    const mockResponse = new HttpResponse({
      body: null as unknown as ServiceConfig,
      status: 404
    });
    return of(mockResponse).pipe(delay(400));
  }

  getApplicationsByServiceId(pagerDutyServiceId: string): Observable<HttpResponse<Application[]>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const mockResponse = new HttpResponse({
      body: [...applications],
      status: 200
    });
    return of(mockResponse).pipe(
      delay(500)
    );
  }

  getApplicationById(pagerDutyServiceId: string, applicationId: string): Observable<HttpResponse<Application | undefined>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const application = applications.find(app => app.applicationId === applicationId);
    const mockResponse = new HttpResponse({
      body: application,
      status: application ? 200 : 404
    });
    return of(mockResponse).pipe(delay(300));
  }

  createApplication(pagerDutyServiceId: string, applicationDto: CreateApplicationDto): Observable<HttpResponse<Application>> {
    const featuresWithIds = applicationDto.features.map(feature => ({
      featureId: `FEAT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...feature
    }));

    const newApplication: Application = {
      applicationId: `APP-${Date.now()}`,
      applicationName: applicationDto.applicationName,
      contactEmail: applicationDto.contactEmail,
      emailDistributions: applicationDto.emailDistributions,
      features: featuresWithIds
    };

    if (!MOCK_APPLICATIONS[pagerDutyServiceId]) {
      MOCK_APPLICATIONS[pagerDutyServiceId] = [];
    }
    MOCK_APPLICATIONS[pagerDutyServiceId].push(newApplication);

    const mockResponse = new HttpResponse({
      body: newApplication,
      status: 201
    });
    return of(mockResponse).pipe(delay(400));
  }

  updateApplication(pagerDutyServiceId: string, applicationId: string, applicationDto: UpdateApplicationDto): Observable<HttpResponse<Application>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const index = applications.findIndex(app => app.applicationId === applicationId);

    if (index !== -1) {
      MOCK_APPLICATIONS[pagerDutyServiceId][index] = {
        ...MOCK_APPLICATIONS[pagerDutyServiceId][index],
        applicationName: applicationDto.applicationName,
        contactEmail: applicationDto.contactEmail,
        emailDistributions: applicationDto.emailDistributions
      };

      const mockResponse = new HttpResponse({
        body: MOCK_APPLICATIONS[pagerDutyServiceId][index],
        status: 200
      });
      return of(mockResponse).pipe(delay(400));
    }

    const mockResponse = new HttpResponse({
      body: null as unknown as Application,
      status: 404
    });
    return of(mockResponse).pipe(delay(400));
  }

  deleteApplication(pagerDutyServiceId: string, applicationId: string): Observable<HttpResponse<void>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const index = applications.findIndex(app => app.applicationId === applicationId);

    if (index !== -1) {
      MOCK_APPLICATIONS[pagerDutyServiceId].splice(index, 1);
      const mockResponse = new HttpResponse<void>({
        status: 204
      });
      return of(mockResponse).pipe(delay(300));
    }

    const mockResponse = new HttpResponse<void>({
      status: 404
    });
    return of(mockResponse).pipe(delay(300));
  }

  getFeatureById(pagerDutyServiceId: string, applicationId: string, featureId: string): Observable<HttpResponse<Feature | undefined>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const application = applications.find(app => app.applicationId === applicationId);
    const feature = application?.features?.find(feat => feat.featureId === featureId);
    const mockResponse = new HttpResponse({
      body: feature,
      status: feature ? 200 : 404
    });
    return of(mockResponse).pipe(delay(300));
  }

  createFeature(pagerDutyServiceId: string, applicationId: string, featureDto: CreateFeatureDto): Observable<HttpResponse<Feature>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const application = applications.find(app => app.applicationId === applicationId);

    if (!application) {
      const mockResponse = new HttpResponse({
        body: null as unknown as Feature,
        status: 404
      });
      return of(mockResponse).pipe(delay(300));
    }

    const newFeature: Feature = {
      featureId: `FEAT-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      featureName: featureDto.featureName,
      contactEmail: featureDto.contactEmail,
      emailDistributions: featureDto.emailDistributions
    };

    application.features.push(newFeature);

    const mockResponse = new HttpResponse({
      body: newFeature,
      status: 201
    });
    return of(mockResponse).pipe(delay(400));
  }

  updateFeature(pagerDutyServiceId: string, applicationId: string, featureId: string, featureDto: UpdateFeatureDto): Observable<HttpResponse<Feature>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const application = applications.find(app => app.applicationId === applicationId);

    if (!application) {
      const mockResponse = new HttpResponse({
        body: null as unknown as Feature,
        status: 404
      });
      return of(mockResponse).pipe(delay(300));
    }

    const featureIndex = application.features.findIndex(feat => feat.featureId === featureId);

    if (featureIndex !== -1) {
      application.features[featureIndex] = {
        ...application.features[featureIndex],
        featureName: featureDto.featureName,
        contactEmail: featureDto.contactEmail,
        emailDistributions: featureDto.emailDistributions
      };

      const mockResponse = new HttpResponse({
        body: application.features[featureIndex],
        status: 200
      });
      return of(mockResponse).pipe(delay(400));
    }

    const mockResponse = new HttpResponse({
      body: null as unknown as Feature,
      status: 404
    });
    return of(mockResponse).pipe(delay(400));
  }

  deleteFeature(pagerDutyServiceId: string, applicationId: string, featureId: string): Observable<HttpResponse<void>> {
    const applications = MOCK_APPLICATIONS[pagerDutyServiceId] || [];
    const application = applications.find(app => app.applicationId === applicationId);

    if (!application) {
      const mockResponse = new HttpResponse<void>({
        status: 404
      });
      return of(mockResponse).pipe(delay(300));
    }

    const featureIndex = application.features.findIndex(feat => feat.featureId === featureId);

    if (featureIndex !== -1) {
      application.features.splice(featureIndex, 1);
      const mockResponse = new HttpResponse<void>({
        status: 204
      });
      return of(mockResponse).pipe(delay(300));
    }

    const mockResponse = new HttpResponse<void>({
      status: 404
    });
    return of(mockResponse).pipe(delay(300));
  }


   getAllIncidents(): Observable<HttpResponse<Incident[]>> {
    const allIncidents = Object.values(MOCK_INCIDENTS).flat();
    const mockResponse = new HttpResponse({
      body: allIncidents,
      status: 200
    });
    return of(mockResponse).pipe(
      delay(500)
    );
  }

  getIncidentsByServiceId(pagerDutyServiceId: string): Observable<HttpResponse<Incident[]>> {
    const allIncidents = Object.values(MOCK_INCIDENTS).flat();
    const filteredIncidents = allIncidents.filter(incident => incident.pagerDutyServiceId === pagerDutyServiceId);

    const mockResponse = new HttpResponse({
      body: [...filteredIncidents],
      status: 200
    });
    return of(mockResponse).pipe(delay(400));
  }

  /**
   * Get all ongoing incidents 
   * @param nextPageToken the next page token to get from 
   * @returns The list of incidents
   */
  getAllOngoingIncidents(nextPageToken?: string): Observable<HttpResponse<PaginatedResponse>> {
    
    let pageResponse = MOCK_PAGINATED_RESPONSES[0]

    if(nextPageToken){
      const pageResponseFound = MOCK_PAGINATED_RESPONSES.find((response) => response.pageId === nextPageToken)
      
      if(pageResponseFound) {
        pageResponse = pageResponseFound
      } else {
        throw new Error("The page response whas not found")
      }
    }

      const mockResponse = new HttpResponse({
      body:pageResponse,
      status: 200
    });
    return of(mockResponse).pipe(delay(400));
  }

   getOngoingIncidentsByServiceId(pagerDutyServiceId: string, statusCode: string): Observable<HttpResponse<Incident[]>> {
    const incidents: Incident[] = [];
    const filteredIncidents = statusCode
      ? incidents.filter(incident => incident.incidentStatusCode === statusCode)
      : incidents;

    const mockResponse = new HttpResponse({
      body: [...filteredIncidents],
      status: 200
    });
    return of(mockResponse).pipe(delay(400));
  }

  /**
   * Get ongoing incidents for a specific service
   */
  getOngoingIncidents(pagerDutyServiceId: string): Observable<HttpResponse<Incident[]>> {
    return this.getOngoingIncidentsByServiceId(pagerDutyServiceId, 'ONGOING');
  }
}
